import { useEffect, useState, useRef } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useI18n } from "@/context/I18nContext";
import { Bell, Heart, UserPlus, Gift, Radio, MessageSquare, FileText, Music2, LifeBuoy, ShieldAlert, Banknote } from "lucide-react";

const ICONS = { follow: UserPlus, tip: Gift, like: Heart, comment: MessageSquare, live: Radio, post: FileText, track: Music2, release: Music2, support: LifeBuoy, moderation: ShieldAlert, payout: Banknote, affiliate: Gift, purchase: Music2 };
const COLORS = {
  follow: "text-[#00F2FE]",
  tip: "text-[#FFD60A]",
  like: "text-[#FF0050]",
  comment: "text-zinc-300",
  live: "text-[#FF0050]",
};

function timeAgo(iso) {
  const d = (Date.now() - new Date(iso).getTime()) / 1000;
  if (d < 60) return "à l'instant";
  if (d < 3600) return `${Math.floor(d / 60)}m`;
  if (d < 86400) return `${Math.floor(d / 3600)}h`;
  return `${Math.floor(d / 86400)}j`;
}

export default function NotificationBell() {
  const { user } = useAuth();
  const { t } = useI18n();
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const boxRef = useRef(null);

  useEffect(() => {
    if (!user) { setUnread(0); setItems([]); return; }
    let mounted = true;
    const tick = async () => {
      try {
        const { data } = await api.get("/notifications/unread-count");
        if (mounted) setUnread(data.count || 0);
      } catch { /* ignore */ }
    };
    tick();
    const id = setInterval(tick, 60000);
    return () => { mounted = false; clearInterval(id); };
  }, [user?.id]);

  useEffect(() => {
    function onClick(e) {
      if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false);
    }
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  const openPanel = async () => {
    setOpen((o) => !o);
    if (!open) {
      try {
        const { data } = await api.get("/notifications?limit=20");
        setItems(data);
      } catch { /* ignore */ }
    }
  };

  const markAll = async () => {
    try {
      await api.post("/notifications/read");
      setItems((arr) => arr.map((x) => ({ ...x, read: true })));
      setUnread(0);
    } catch { /* ignore */ }
  };

  if (!user) return null;

  const labels = t.notifications || {
    title: "Notifications",
    empty: "Aucune notification.",
    markAll: "Tout marquer lu",
    follow: "vous suit maintenant",
    tip: "vous a envoyé un pourboire",
    like: "a aimé votre publication",
    comment: "a commenté votre publication",
    live: "est en direct",
  };

  return (
    <div ref={boxRef} className="relative">
      <button
        onClick={openPanel}
        className="relative p-2 rounded-full hover:bg-white/10 transition-colors"
        aria-label="notifications"
        data-testid="notif-bell"
      >
        <Bell className="w-5 h-5 text-zinc-300" />
        {unread > 0 && (
          <span className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 rounded-full bg-[#FF0050] text-white text-[10px] font-black grid place-items-center" data-testid="notif-badge">
            {unread > 9 ? "9+" : unread}
          </span>
        )}
      </button>
      {open && (
        <div className="absolute right-0 mt-2 w-80 bg-zinc-950/95 border border-white/10 rounded-2xl backdrop-blur-xl shadow-2xl overflow-hidden z-50 anim-in" data-testid="notif-panel">
          <div className="flex items-center justify-between px-4 pt-3 pb-2 border-b border-white/5">
            <h4 className="text-sm font-bold text-white">{labels.title}</h4>
            <button onClick={markAll} className="text-[11px] text-[#00F2FE] hover:underline" data-testid="notif-markall">{labels.markAll}</button>
          </div>
          <div className="max-h-[60vh] overflow-y-auto">
            {items.length === 0 && (
              <div className="p-6 text-center text-sm text-zinc-500">{labels.empty}</div>
            )}
            {items.map((n) => (
              <NotifRow key={n.id} n={n} labels={labels} />
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function NotifRow({ n, labels }) {
  const Icon = ICONS[n.kind] || Bell;
  const color = COLORS[n.kind] || "text-zinc-300";
  let text = "";
  if (n.kind === "follow") text = labels.follow;
  else if (n.kind === "tip") text = `${labels.tip} : ${n.data?.amount || 0} ${n.data?.currency || "FCFA"}`;
  else if (n.kind === "like") text = labels.like;
  else if (n.kind === "comment") text = labels.comment;
  else if (n.kind === "live") text = labels.live;
  else if (n.kind === "support") text = "a répondu à votre message";
  else if (n.kind === "moderation") text = n.data?.message || "Décision de modération";
  else if (n.kind === "payout" || n.kind === "affiliate" || n.kind === "purchase") text = n.data?.message || "";
  else if (n.kind === "post") text = labels.post || "a publié";
  else if (n.kind === "track") text = labels.track || "a sorti un nouveau morceau";
  else if (n.kind === "release") text = "a sorti un nouveau projet";

  const inner = (
    <div className={`flex items-start gap-3 px-4 py-3 hover:bg-white/5 transition-colors ${n.read ? "" : "bg-white/[0.03]"}`}>
      <div className={`w-9 h-9 rounded-full bg-white/5 grid place-items-center ${color}`}>
        <Icon className="w-4 h-4" />
      </div>
      <div className="flex-1 min-w-0">
        <div className="text-sm text-white">
          <span className="font-bold">{["support", "moderation", "payout", "affiliate", "purchase"].includes(n.kind) ? "L'équipe Metazik" : (n.actor_name || "Quelqu'un")}</span>{" "}
          <span className="text-zinc-400">{text}</span>
        </div>
        <div className="text-[11px] text-zinc-500 mt-0.5">{timeAgo(n.created_at)}</div>
      </div>
      {!n.read && <span className="w-2 h-2 rounded-full bg-[#FF0050] mt-2" />}
    </div>
  );

  if (n.kind === "support") {
    return <Link to="/support" data-testid={`notif-${n.id}`}>{inner}</Link>;
  }
  if (n.actor_id && (n.kind === "follow" || n.kind === "live" || n.kind === "post" || n.kind === "track" || n.kind === "release")) {
    return <Link to={`/profile/${n.actor_id}`} data-testid={`notif-${n.id}`}>{inner}</Link>;
  }
  return <div data-testid={`notif-${n.id}`}>{inner}</div>;
}
